import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { HubConnectionBuilder, LogLevel, type HubConnection } from '@microsoft/signalr';
import { API_BASE_URL } from '../config/apiConfig';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { apiClient } from '../services/api';
import { logger } from '../services/logger';
import { afterInitialPaint } from '../utils/afterInitialPaint';
import { NotificationType, type Notification } from '../types/notification';

interface NotificationContextValue {
  unreadCount: number;
  notifications: Notification[];
  isOpen: boolean;
  isLoading: boolean;
  togglePanel: () => void;
  closePanel: () => void;
  fetchNotifications: (page?: number, pageSize?: number) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

interface DiscoveryNotificationMetadata {
  discoverySourceId?: string;
}

const notificationTypeIconMap: Record<number, string> = {
  [NotificationType.IncidentCreated]: '🆕',
  [NotificationType.IncidentAssigned]: '👤',
  [NotificationType.IncidentTeamAssigned]: '👥',
  [NotificationType.IncidentStatusChanged]: '🔄',
  [NotificationType.IncidentResolved]: '✅',
  [NotificationType.IncidentEscalated]: '⚠️',
  [NotificationType.IncidentSlaBreached]: '🚨',
  [NotificationType.IncidentSlaWarning]: '⏳',
  [NotificationType.IncidentPriorityChanged]: '📌',
  [NotificationType.IncidentMajorIncidentChanged]: '🔥',
  [NotificationType.IncidentCommentAdded]: '💬',
  [NotificationType.DiscoveryJobStarted]: '🔄',
  [NotificationType.DiscoveryJobCompleted]: '✅',
  [NotificationType.DiscoveryJobFailed]: '❌',
  [NotificationType.DiscoveryJobCancelled]: '⏹️',
  [NotificationType.OnCallAlert]: '📟',
  [NotificationType.EscalationAlert]: '🚨',
  [NotificationType.OnCallShiftStarting]: '🔔',
  [NotificationType.OnCallShiftEnding]: '🔕',
  [NotificationType.TeamMemberAdded]: '👥',
  [NotificationType.TeamMemberRemoved]: '👥',
  [NotificationType.OnCallParticipantAdded]: '📟',
  [NotificationType.OnCallParticipantRemoved]: '📟',
  [NotificationType.UserRoleChanged]: '🔑',
  [NotificationType.AgreementReviewOverdue]: '📅',
  [NotificationType.AiBudgetThresholdReached]: '💳',
};
const TOAST_DISPATCH_INTERVAL_MS = 100;

function parseDiscoveryMetadata(metadata: string | null): DiscoveryNotificationMetadata {
  if (!metadata) {
    return {};
  }

  try {
    return (JSON.parse(metadata) as DiscoveryNotificationMetadata) ?? {};
  } catch {
    return {};
  }
}

function getNotificationPath(notification: Notification): string | null {
  if (notification.sourceEntityType === 'Incident') {
    return `/incidents/${notification.sourceEntityId}`;
  }

  if (notification.sourceEntityType === 'DiscoveryJob') {
    const metadata = parseDiscoveryMetadata(notification.metadata);
    if (metadata.discoverySourceId) {
      return `/admin/integrations/discovery-jobs/${metadata.discoverySourceId}/${notification.sourceEntityId}`;
    }
    return '/admin/integrations/discovery-jobs';
  }

  if (notification.sourceEntityType === 'Agreement') {
    return `/admin/itil-configuration?tab=agreements&agreementId=${notification.sourceEntityId}`;
  }

  return null;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);
  const notificationIdsRef = useRef<Set<string>>(new Set());
  const pendingLiveNotificationsRef = useRef<Map<string, Notification>>(new Map());
  const initialNotificationsPendingRef = useRef(true);
  const unreadCountRevisionRef = useRef(0);
  const activeIdentityRef = useRef<string | null>(null);
  const toastQueueRef = useRef<Notification[]>([]);
  const toastDispatchTimerRef = useRef<number | null>(null);

  const sortNewestFirst = useCallback((items: Notification[]) => {
    return [...items].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.tenantId) {
      setUnreadCount(0);
      return;
    }

    const revision = unreadCountRevisionRef.current;
    const count = await apiClient.getUnreadNotificationCount(user.tenantId);
    if (revision === unreadCountRevisionRef.current) {
      setUnreadCount(count);
    }
  }, [user?.tenantId]);

  const fetchNotifications = useCallback(
    async (page = 1, pageSize = 20) => {
      if (!user?.tenantId) {
        setNotifications([]);
        return;
      }

      try {
        const items = await apiClient.getNotifications(user.tenantId, page, pageSize, true);
        if (initialNotificationsPendingRef.current) {
          const merged = new Map(items.map((item) => [item.id, item]));
          pendingLiveNotificationsRef.current.forEach((item, id) => merged.set(id, item));
          pendingLiveNotificationsRef.current.clear();
          initialNotificationsPendingRef.current = false;
          setNotifications(sortNewestFirst([...merged.values()]));
        } else {
          setNotifications(sortNewestFirst(items));
        }
      } catch (error) {
        if (initialNotificationsPendingRef.current) {
          initialNotificationsPendingRef.current = false;
          setNotifications(sortNewestFirst([...pendingLiveNotificationsRef.current.values()]));
          pendingLiveNotificationsRef.current.clear();
        }
        const apiError = error as { response?: { status?: number } };
        if (apiError.response?.status === 500) {
          logger.warn('Notifications endpoint failed. Returning empty list until migration is applied.', {
            tenantId: user.tenantId,
            page,
            pageSize,
          });
          setNotifications([]);
          return;
        }

        logger.error('Failed to fetch notifications', error, {
          tenantId: user.tenantId,
          page,
          pageSize,
        });
        setNotifications([]);
      }
    },
    [sortNewestFirst, user?.tenantId],
  );

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const flushToastQueue = useCallback(() => {
    if (toastDispatchTimerRef.current !== null) {
      return;
    }

    const dispatchNextToast = () => {
      const nextNotification = toastQueueRef.current.shift();
      if (!nextNotification) {
        toastDispatchTimerRef.current = null;
        return;
      }

      const targetPath = getNotificationPath(nextNotification);
      const icon = notificationTypeIconMap[nextNotification.type] ?? '🔔';
      addToast(nextNotification.message, 'info', {
        title: nextNotification.title,
        icon,
        variant: 'notification',
        actions: targetPath
          ? [
              {
                label: 'View',
                onClick: () => {
                  window.location.assign(targetPath);
                },
              },
              {
                label: 'Dismiss',
                onClick: () =>
                  logger.debug('Notification toast dismissed', { notificationId: nextNotification.id }),
              },
            ]
          : [
              {
                label: 'Dismiss',
                onClick: () =>
                  logger.debug('Notification toast dismissed', { notificationId: nextNotification.id }),
              },
            ],
      });

      toastDispatchTimerRef.current = window.setTimeout(dispatchNextToast, TOAST_DISPATCH_INTERVAL_MS);
    };

    dispatchNextToast();
  }, [addToast]);

  const enqueueNotificationToast = useCallback(
    (notification: Notification) => {
      toastQueueRef.current.push(notification);
      flushToastQueue();
    },
    [flushToastQueue],
  );

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!user?.tenantId) {
        return;
      }

      let unreadWasDecremented = false;
      setNotifications((prev) =>
        prev.map((item) => {
          if (item.id !== notificationId || item.isRead) {
            return item;
          }

          unreadWasDecremented = true;
          return {
            ...item,
            isRead: true,
            readAt: new Date().toISOString(),
          };
        }),
      );

      if (unreadWasDecremented) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      try {
        await apiClient.markNotificationRead(user.tenantId, notificationId);
      } catch (error) {
        logger.error('Failed to mark notification as read', error, {
          tenantId: user.tenantId,
          notificationId,
        });
        await Promise.all([fetchUnreadCount(), fetchNotifications()]);
      }
    },
    [fetchNotifications, fetchUnreadCount, user?.tenantId],
  );

  const markAllAsRead = useCallback(async () => {
    if (!user?.tenantId) {
      return;
    }

    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        isRead: true,
        readAt: item.readAt ?? now,
      })),
    );
    setUnreadCount(0);

    try {
      await apiClient.markAllNotificationsRead(user.tenantId);
    } catch (error) {
      logger.error('Failed to mark all notifications as read', error, { tenantId: user.tenantId });
      await Promise.all([fetchUnreadCount(), fetchNotifications()]);
    }
  }, [fetchNotifications, fetchUnreadCount, user?.tenantId]);

  const clearAll = useCallback(async () => {
    if (!user?.tenantId) {
      return;
    }

    const previousNotifications = notifications;
    setNotifications([]);
    setUnreadCount(0);

    try {
      await apiClient.dismissAllNotificationsFromPanel(user.tenantId);
    } catch (error) {
      logger.error('Failed to dismiss all notifications from panel', error, {
        tenantId: user.tenantId,
        attemptedCount: previousNotifications.length,
      });
      await Promise.all([fetchUnreadCount(), fetchNotifications()]);
    }
  }, [fetchNotifications, fetchUnreadCount, notifications, user?.tenantId]);

  const deleteNotification = useCallback(
    async (notificationId: string) => {
      if (!user?.tenantId) {
        return;
      }

      let unreadWasDeleted = false;
      setNotifications((prev) =>
        prev.filter((item) => {
          if (item.id !== notificationId) {
            return true;
          }

          unreadWasDeleted = !item.isRead;
          return false;
        }),
      );

      if (unreadWasDeleted) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      try {
        await apiClient.deleteNotification(user.tenantId, notificationId);
      } catch (error) {
        logger.error('Failed to delete notification', error, {
          tenantId: user.tenantId,
          notificationId,
        });
        await Promise.all([fetchUnreadCount(), fetchNotifications()]);
      }
    },
    [fetchNotifications, fetchUnreadCount, user?.tenantId],
  );

  useEffect(() => {
    notificationIdsRef.current = new Set(notifications.map((item) => item.id));
  }, [notifications]);

  useEffect(() => {
    let isCancelled = false;

    const stopConnection = async () => {
      const existingConnection = connectionRef.current;
      if (!existingConnection) {
        return;
      }

      connectionRef.current = null;
      try {
        await existingConnection.stop();
        logger.info('Notification SignalR connection stopped');
      } catch (error) {
        logger.warn('Failed to stop notification SignalR connection', { error });
      }
    };

    const initialize = async () => {
      await stopConnection();

      const identity = user?.tenantId && user.userId ? `${user.tenantId}:${user.userId}` : null;
      if (activeIdentityRef.current !== identity) {
        activeIdentityRef.current = identity;
        unreadCountRevisionRef.current += 1;
        setUnreadCount(0);
        setNotifications([]);
        setIsOpen(false);
        notificationIdsRef.current.clear();
        pendingLiveNotificationsRef.current.clear();
        initialNotificationsPendingRef.current = true;
      }

      if (!user?.tenantId) {
        setUnreadCount(0);
        setNotifications([]);
        setIsOpen(false);
        notificationIdsRef.current.clear();
        pendingLiveNotificationsRef.current.clear();
        toastQueueRef.current = [];
        if (toastDispatchTimerRef.current !== null) {
          window.clearTimeout(toastDispatchTimerRef.current);
          toastDispatchTimerRef.current = null;
        }
        return;
      }

      logger.info('Initializing notifications', { tenantId: user.tenantId, userId: user.userId });

      void afterInitialPaint().then(async () => {
        if (isCancelled) {
          return;
        }

        setIsLoading(true);
        try {
          await Promise.all([fetchUnreadCount(), fetchNotifications()]);
        } catch (error) {
          logger.error('Failed to fetch initial notifications', error, { tenantId: user.tenantId });
        } finally {
          if (!isCancelled) {
            setIsLoading(false);
          }
        }
      });

      const connection = new HubConnectionBuilder()
        .withUrl(`${API_BASE_URL}/hubs/notifications`, {
          accessTokenFactory: () => localStorage.getItem('auth_token') ?? '',
        })
        .withAutomaticReconnect([0, 1000, 3000, 10000, 30000])
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('ReceiveNotification', (notification: Notification) => {
        logger.info('Received notification via SignalR', { notificationId: notification.id });
        const isNewNotification = !notificationIdsRef.current.has(notification.id);
        if (initialNotificationsPendingRef.current) {
          pendingLiveNotificationsRef.current.set(notification.id, notification);
        }
        setNotifications((prev) => {
          const existingIndex = prev.findIndex((item) => item.id === notification.id);
          if (existingIndex === -1) {
            return sortNewestFirst([notification, ...prev]);
          }

          const updated = [...prev];
          updated[existingIndex] = notification;
          return sortNewestFirst(updated);
        });

        if (!isNewNotification) {
          return;
        }
        notificationIdsRef.current.add(notification.id);
        enqueueNotificationToast(notification);
      });

      connection.on('ReceiveUnreadCount', (count: number) => {
        logger.debug('Received unread count update via SignalR', { count });
        unreadCountRevisionRef.current += 1;
        setUnreadCount(count);
      });

      connection.onreconnecting((error) => {
        logger.warn('Notification SignalR reconnecting', { error });
      });

      connection.onreconnected(() => {
        logger.info('Notification SignalR reconnected');
      });

      connection.onclose((error) => {
        logger.warn('Notification SignalR connection closed', { error });
      });

      connectionRef.current = connection;

      try {
        await connection.start();
        logger.info('Notification SignalR connection started');
      } catch (error) {
        logger.error('Failed to start notification SignalR connection', error, {
          tenantId: user.tenantId,
        });
      }
    };

    void initialize();

    return () => {
      isCancelled = true;
      toastQueueRef.current = [];
      if (toastDispatchTimerRef.current !== null) {
        window.clearTimeout(toastDispatchTimerRef.current);
        toastDispatchTimerRef.current = null;
      }
      void stopConnection();
    };
  }, [enqueueNotificationToast, fetchNotifications, fetchUnreadCount, sortNewestFirst, user?.tenantId, user?.userId]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      unreadCount,
      notifications,
      isOpen,
      isLoading,
      togglePanel,
      closePanel,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      clearAll,
      deleteNotification,
    }),
    [
      unreadCount,
      notifications,
      isOpen,
      isLoading,
      togglePanel,
      closePanel,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      clearAll,
      deleteNotification,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
